/**
 * `AccessZoneService` — `/api/v1/events/{eventId}/access-zones/*`.
 *
 * ⚠️ **ЭТО НЕ ПРАВА ДОСТУПА.** Зона доступа — участок ПЛОЩАДКИ: раздевалки,
 * зона разминки, микст-зона, техническая зона. Она описывает здание, а не то,
 * что человек может сделать в приложении. Права по-прежнему описывают
 * `auth.isAdmin` и `event.canManage`, и ни одна проверка не имеет права
 * смотреть сюда при рисовании кнопок.
 *
 * Справочник **локален** для мероприятия: у каждого соревнования своё здание.
 *
 * ПРАВА те же, что и на мероприятие: admin и owner наполняют, viewer читает,
 * не участник получает 404 `EVENT_NOT_FOUND`.
 */

import { http } from '@/lib/http'
import type {
  CreateAccessZoneRequest,
  CreateAccessZoneResponse,
  DeleteAccessZoneResponse,
  ListAccessZonesResponse,
  UpdateAccessZoneRequest,
  UpdateAccessZoneResponse,
} from '@/types/api'

function path(eventId: string, suffix = ''): string {
  return `/api/v1/events/${encodeURIComponent(eventId)}/access-zones${suffix}`
}

export const accessZonesApi = {
  /**
   * `GET .../access-zones` — весь справочник, **по триграмме**. Читает любой
   * участник мероприятия.
   *
   * Пагинации нет намеренно: это десятки записей, из которых собирают набор
   * отметок на бейдже, а не лента.
   */
  async list(eventId: string, signal?: AbortSignal): Promise<ListAccessZonesResponse> {
    const { data } = await http.get<ListAccessZonesResponse>(path(eventId), { signal })
    return data
  },

  /**
   * `POST .../access-zones` — admin или owner.
   *
   * Два разных 409, и различать их обязательно:
   *   `ACCESS_ZONE_ALREADY_EXISTS` с `metadata.name` — занято название;
   *   `ACCESS_ZONE_ABBREVIATION_ALREADY_EXISTS` с `metadata.abbreviation` —
   *   занята триграмма.
   *
   * Сервер поднимает регистр триграммы и опускает регистр цвета сам, поэтому
   * `air` и `#AABBCC` — валидный ввод. Форма делает это же превращение на
   * лету, чтобы человек видел итог до отправки, а не после.
   */
  async create(
    eventId: string,
    body: CreateAccessZoneRequest,
  ): Promise<CreateAccessZoneResponse> {
    const { data } = await http.post<CreateAccessZoneResponse>(path(eventId), body)
    return data
  },

  /**
   * `PUT .../access-zones/{id}` — все три поля разом.
   *
   * PUT, а не PATCH: изменяемых полей ровно три, все три всегда есть в форме, и
   * зоны с названием, но без цвета не бывает.
   */
  async update(
    eventId: string,
    id: string,
    body: UpdateAccessZoneRequest,
  ): Promise<UpdateAccessZoneResponse> {
    const { data } = await http.put<UpdateAccessZoneResponse>(
      path(eventId, `/${encodeURIComponent(id)}`),
      body,
    )
    return data
  },

  /**
   * `DELETE .../access-zones/{id}` — жёсткое удаление.
   *
   * Сейчас на зону не ссылается ничто, поэтому удаление ничего за собой не
   * тянет. ⚠️ Когда появится привязка зон к ролям участников, это перестанет
   * быть правдой — и подтверждение здесь обязано будет назвать последствие,
   * как это уже делает подтверждение удаления роли.
   */
  async remove(eventId: string, id: string): Promise<DeleteAccessZoneResponse> {
    const { data } = await http.delete<DeleteAccessZoneResponse>(
      path(eventId, `/${encodeURIComponent(id)}`),
    )
    return data
  },
}
